import { useContext } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import {
  Anchor,
  Box,
  Heading,
  Image,
  Paragraph,
  ResponsiveContext,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
} from 'grommet';

import YouTubeEmbed, { youTubeId } from './YouTubeEmbed';

/**
 * A paragraph whose only content is a bare YouTube link becomes an inline
 * player. Keeping the convention to "a plain URL on its own line" means the
 * markdown stays readable and degrades to a working link if this ever changes.
 */
function youTubeUrlFromParagraph(node) {
  const children = (node?.children ?? []).filter(
    (child) => !(child.type === 'text' && child.value.trim() === '')
  );
  if (children.length !== 1) return null;

  const [only] = children;
  if (only.type === 'text') return youTubeId(only.value.trim()) ? only.value.trim() : null;
  // remark-gfm autolinks bare URLs, so the child is usually a link node.
  if (only.type === 'element' && only.tagName === 'a') {
    const href = only.properties?.href;
    return youTubeId(href) ? href : null;
  }
  return null;
}

const isInternal = (href) => typeof href === 'string' && href.startsWith('/');

/** True when a paragraph holds nothing but an image. */
function isImageOnly(node) {
  const children = (node?.children ?? []).filter(
    (child) => !(child.type === 'text' && child.value.trim() === '')
  );
  return children.length === 1
    && children[0].type === 'element'
    && children[0].tagName === 'img';
}

function makeComponents(isSmallScreen) {
  const heading = (level) => function MdHeading({ node, children, ...props }) {
    return (
      <Heading
        level={level}
        size="small"
        margin={{ top: level <= 2 ? 'medium' : 'small', bottom: 'xsmall' }}
        {...props}
      >
        {children}
      </Heading>
    );
  };

  return {
    h1: heading(1),
    h2: heading(2),
    h3: heading(3),
    h4: heading(4),
    h5: heading(5),
    h6: heading(6),

    p({ node, children, ...props }) {
      const youTubeUrl = youTubeUrlFromParagraph(node);
      if (youTubeUrl) return <YouTubeEmbed id={youTubeId(youTubeUrl)} />;
      // Markdown wraps a standalone image in a paragraph. Rendering it as a
      // block needs a <div>, which is invalid inside <p>, so the paragraph
      // itself becomes the block in that case.
      if (isImageOnly(node)) return <Box margin={{ vertical: 'medium' }} flex={false} align="start">{children}</Box>;
      return <Paragraph fill margin={{ vertical: 'small' }} {...props}>{children}</Paragraph>;
    },

    a({ node, href, children, ...props }) {
      if (isInternal(href)) {
        return <Anchor as={Link} to={href} {...props}>{children}</Anchor>;
      }
      return (
        <Anchor href={href} target="_blank" rel="noreferrer noopener" {...props}>
          {children}
        </Anchor>
      );
    },

    ul: ({ node, ...props }) => (
      <Box as="ul" margin={{ vertical: 'small', left: 'medium' }} gap="xsmall" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <Box as="ol" margin={{ vertical: 'small', left: 'medium' }} gap="xsmall" {...props} />
    ),
    li: ({ node, children, ...props }) => (
      <Text as="li" {...props}>{children}</Text>
    ),

    blockquote: ({ node, children, ...props }) => (
      <Box
        background="light-1"
        border={{ side: 'left', size: 'medium', color: 'brand' }}
        pad={{ horizontal: 'medium', vertical: 'small' }}
        margin={{ vertical: 'medium' }}
        round={{ size: 'xsmall', corner: 'right' }}
        {...props}
      >
        {children}
      </Box>
    ),

    // The archive tables run to 13 columns, so each one scrolls inside its own
    // container rather than making the whole page scroll sideways on a phone.
    table: ({ node, children, ...props }) => (
      <Box overflow={{ horizontal: 'auto' }} margin={{ vertical: 'medium' }} flex={false}>
        <Table {...props}>{children}</Table>
      </Box>
    ),
    thead: ({ node, ...props }) => <TableHeader {...props} />,
    tbody: ({ node, ...props }) => <TableBody {...props} />,
    tr: ({ node, ...props }) => <TableRow {...props} />,
    th: ({ node, children, style, ...props }) => (
      <TableCell scope="col" border="bottom" pad={isSmallScreen ? 'xsmall' : 'small'} {...props}>
        <Text weight="bold" size={isSmallScreen ? 'small' : 'medium'}>{children}</Text>
      </TableCell>
    ),
    td: ({ node, children, style, ...props }) => (
      <TableCell pad={isSmallScreen ? 'xsmall' : 'small'} {...props}>
        <Text size={isSmallScreen ? 'small' : 'medium'}>{children}</Text>
      </TableCell>
    ),

    img: ({ node, src, alt, ...props }) => (
      <Image src={src} alt={alt ?? ''} fit="contain" style={{ maxWidth: '100%' }} {...props} />
    ),

    hr: () => (
      <Box border={{ side: 'bottom', color: 'light-4' }} margin={{ vertical: 'medium' }} flex={false} />
    ),

    code: ({ node, children, ...props }) => (
      <Text as="code" size="small" {...props}>{children}</Text>
    ),
    pre: ({ node, children, ...props }) => (
      <Box
        as="pre"
        background="light-2"
        pad="small"
        round="xsmall"
        overflow={{ horizontal: 'auto' }}
        margin={{ vertical: 'medium' }}
        {...props}
      >
        {children}
      </Box>
    ),
  };
}

/**
 * Renders a league doc. remark-gfm supplies the tables the content leans on;
 * rehype-slug adds the heading ids that the table of contents links to.
 */
export default function MarkdownDoc({ children }) {
  const size = useContext(ResponsiveContext);
  const isSmallScreen = size === 'small' || size === 'xsmall';

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSlug]}
      components={makeComponents(isSmallScreen)}
    >
      {children}
    </ReactMarkdown>
  );
}
